import * as React from "react";

import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { trpc } from "@editor/utils/trpc";

import { skipToken as reactQuerySkipToken } from "@tanstack/react-query";
import { BillingTiers } from "schemas/billing";

const useAnalyticsStatus = () => {
  const workspaceId = useCurrentWorkspaceId() ?? undefined;
  const projectId = useCurrentProjectId();

  const { data: isEnabled, isLoading: isLoadingEnableCheck } =
    trpc.analytics.isEnabled.useQuery(
      workspaceId || projectId
        ? { workspaceId, projectId }
        : reactQuerySkipToken,
    );

  const { data: projectSubscription, isLoading: isLoadingProjectSubscription } =
    trpc.subscriptions.getActiveSubscriptionByProject.useQuery(
      projectId ?? reactQuerySkipToken,
    );

  const {
    data: workspaceSubscription,
    isLoading: isLoadingWorkspaceSubscription,
  } = trpc.subscriptions.getActiveSubscriptionByWorkspace.useQuery(
    // NOTE (Max, 2025-06-05): If we have projectId, we don't need to fetch the workspace subscription
    workspaceId && !projectId ? workspaceId : reactQuerySkipToken,
  );

  const subscription = projectSubscription ?? workspaceSubscription;
  const isLoadingSubscription =
    isLoadingProjectSubscription || isLoadingWorkspaceSubscription;

  const isCurrentlyPaying =
    Boolean(subscription) && subscription?.name !== BillingTiers.FREE;

  const isLoading = isLoadingEnableCheck || isLoadingSubscription;

  const dataDisplayType = (() => {
    if (isEnabled && !isCurrentlyPaying) {
      return "partialData";
    } else if (!isEnabled && isCurrentlyPaying) {
      return "blurFullScreenRequireShopifyConnect";
    } else if (!isEnabled && !isCurrentlyPaying) {
      return "blurFullScreenRequireUpgrade";
    } else {
      return "normal";
    }
  })();

  return {
    isEnabled,
    isCurrentlyPaying,
    isLoading,
    dataDisplayType,
    shouldBlurFullScreen:
      dataDisplayType === "blurFullScreenRequireShopifyConnect" ||
      dataDisplayType === "blurFullScreenRequireUpgrade",
  };
};

export default useAnalyticsStatus;
